/**
 * P3-S5 보호자 공유(SR-REC-05 · TC-REC-05 · SCR v0.7 검토 §3.4 4-7/4-8 · §3.5 5-1~5-9) — 제어 평면 계약(P3-S5 계약 §1):
 *
 * 설정(RuntimeConfig · GET /api/runtime-config → {config}):
 *   features.observerShare(기본 false — 출시 OFF · D-17) · share.maxDays(기본 30 · 한도 90) · share.otherNames("all" | "friend" · 기본 friend)
 *
 * 교사(플래그 ON · 코스 교사 — OFF 면 404 → 영역 숨김):
 *   GET  /api/courses/{c}/teacher/shares?replayId=|jobId= → {items:[Item], maxDays, otherNames}
 *        Item = {linkId, target, replayId, jobId|null, child:{pid, nickname}, createdAt, expiresAt, openCount,
 *                firstOpenedAt|null, lastOpenedAt|null, revokedAt|null, state: active|expired|revoked}
 *   POST …/teacher/shares {target, replayId?(replay), jobId?(video · 완료 작업), childPid, days?} → 201 {...Item, url}(주소는 이때 한 번만)
 *        · 422 {field: days|target|replayId|jobId|childPid} · friend 정책에서 영상 = 422 {field:"target", reason:"policy"}
 *        · 410 FE-504 만료 리플레이
 *   POST …/teacher/shares/{linkId}/revoke → 200 Item(여러 번 불러도 같다)
 *
 * 관찰자(Bearer 는 있으면 붙인다):
 *   Mentor 런치 → `${shell}/share/<token>#ticket=<t>`(플래그 OFF 면 티켓 없이) · 대상 없는 런치는 /share/-(404)
 *   GET /api/share/{token} → 플래그 OFF 200 {enabled:false} · 세션 없음 200 {enabled:true, session:false, campusUrl}
 *        · 관찰자 아닌 세션 403 {code:FE-302, reason:"role"} · 기관 밖 403 {reason:"org"} · 자녀 아님 403 {reason:"child"}
 *        · 모르는 토큰 404 · 취소 410 {state:"revoked"} · 만료 410 {state:"expired"} · friend 정책의 영상 링크 410 {state:"policy"}
 *        · 200 {enabled:true, session:true, linkId, course:{label}, activity:{title}, child:{nickname}, target, expiresAt,
 *               otherNames, video?:{url(절대 · 서명 30분), durationS, expiresAt}, replay?:{replayId, format}}
 *   리플레이 재생(target replay 만 · 아니면 404):
 *   GET /api/share/{token}/replay → {replayId, format, modeId, presetKey, snapshotIds, meta(pid 없음),
 *        machines:[{machine, snapshotId, nickname|null, mine}]}
 *   GET …/replay/file → 바이트 · GET …/replay/bundles/{snapshotId} → RunBundle
 *   관찰자는 /api/courses/** 403 — 런타임 설정 · 팔레트 · 모드 · 카탈로그만 읽는다
 */
import { useMemo } from "react";
import type { RunBundle } from "@picnic-field/local-host/protocol";
import { ApiError } from "../../../lib/http/errors";
import { readConfig } from "../../../lib/config";
import { useSession, useSessionManager } from "../../../store/session/SessionProvider";
import type { BytesRequester } from "../../../types/api";
import type {
  OtherNames,
  ShareSettings,
  ShareLink,
  ShareCreated,
  ShareList,
  ShareCreate,
  ShareOpen,
  ShareMachine,
  ShareReplay,
} from "../types";

export const SHARE_DEFAULTS: ShareSettings = { enabled: false, maxDays: 30, otherNames: "friend" };
/** 바깥 한도(계약 — share.maxDays 는 90 을 넘지 못한다) */
export const SHARE_MAX_DAYS_CAP = 90;

const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const num = (v: unknown): number | null => {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

/** 런타임 설정 응답({config} 또는 설정 그대로)에서 공유 설정을 너그럽게 읽는다 — 모르면 OFF */
export function readShareSettings(v: unknown): ShareSettings {
  const root = obj(v);
  const cfg = obj(root?.config) ?? root ?? {};
  // 중첩({features:{observerShare}}) · 평평한 키("features.observerShare") 둘 다
  const pick = (group: string, key: string): unknown =>
    obj(cfg[group])?.[key] ?? cfg[`${group}.${key}`];
  const days = num(pick("share", "maxDays"));
  return {
    enabled: pick("features", "observerShare") === true,
    maxDays:
      days !== null && days >= 1
        ? Math.min(SHARE_MAX_DAYS_CAP, Math.floor(days))
        : SHARE_DEFAULTS.maxDays,
    otherNames: pick("share", "otherNames") === "all" ? "all" : "friend",
  };
}

// ── 교사 ────────────────────────────────────────────────────────────────

/** friend 정책에서 영상 링크를 만들려 했다(422 policy) */
export class SharePolicyError extends Error {
  constructor() {
    super("share policy");
    this.name = "SharePolicyError";
  }
}

export function readLink(v: unknown): ShareLink | null {
  const o = obj(v);
  const linkId = str(o?.linkId) ?? (typeof o?.linkId === "number" ? String(o.linkId) : null);
  if (!o || !linkId) return null;
  const child = obj(o.child);
  const state = o.state === "expired" || o.state === "revoked" ? o.state : "active";
  // 만료 시각이 지났는데 서버가 active 로 줬어도 화면은 만료로(목록을 오래 열어 둔 경우)
  const exp = str(o.expiresAt);
  const late = state === "active" && exp !== null && Date.parse(exp) <= Date.now();
  return {
    linkId,
    target: o.target === "replay" ? "replay" : "video",
    replayId: str(o.replayId),
    jobId: str(o.jobId) ?? (typeof o.jobId === "number" ? String(o.jobId) : null),
    child: { pid: str(child?.pid) ?? "", nickname: str(child?.nickname) ?? "" },
    createdAt: str(o.createdAt),
    expiresAt: exp,
    openCount: Math.max(0, Math.floor(num(o.openCount) ?? 0)),
    lastOpenedAt: str(o.lastOpenedAt),
    state: late ? "expired" : state,
  };
}

export function readLinks(v: unknown): ShareLink[] {
  const list = Array.isArray(v) ? v : obj(v)?.items;
  return (Array.isArray(list) ? list : []).map(readLink).filter((l): l is ShareLink => !!l);
}

/** 상대 주소(/share/…)면 셸 원점을 붙인다 — 복사해 보내는 주소는 절대 주소여야 한다 */
export function absoluteUrl(url: string, origin: string): string {
  return url.startsWith("/") && !url.startsWith("//") ? `${origin.replace(/\/+$/, "")}${url}` : url;
}

export interface ShareTeacherApi {
  settings(): Promise<ShareSettings>;
  /** 플래그 OFF(404)면 null — 영역을 숨긴다 */
  list(filter: { replayId?: string | null; jobId?: string | null }): Promise<ShareList | null>;
  create(body: ShareCreate): Promise<ShareCreated>;
  revoke(linkId: string): Promise<ShareLink | null>;
}

export function createShareTeacherApi(
  request: BytesRequester,
  courseId: string,
  origin = typeof window !== "undefined" ? window.location.origin : "",
): ShareTeacherApi {
  const c = `/api/courses/${encodeURIComponent(courseId)}/teacher/shares`;
  const post = (body?: unknown): RequestInit => ({
    method: "POST",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  return {
    async settings() {
      try {
        return readShareSettings(await request<unknown>("/api/runtime-config"));
      } catch {
        return SHARE_DEFAULTS;
      }
    },
    async list(filter) {
      const q = new URLSearchParams();
      if (filter.replayId) q.set("replayId", filter.replayId);
      if (filter.jobId) q.set("jobId", filter.jobId);
      const qs = q.toString();
      try {
        const v = await request<unknown>(qs ? `${c}?${qs}` : c);
        const o = obj(v);
        const days = num(o?.maxDays);
        return {
          items: readLinks(v),
          maxDays:
            days !== null && days >= 1 ? Math.min(SHARE_MAX_DAYS_CAP, Math.floor(days)) : null,
          otherNames:
            o?.otherNames === "all" ? "all" : o?.otherNames === "friend" ? "friend" : null,
        };
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    async create(body) {
      let v: unknown;
      try {
        v = await request<unknown>(c, post(body));
      } catch (e) {
        const b = obj(e instanceof ApiError ? e.body : null);
        if (e instanceof ApiError && e.status === 422 && b?.reason === "policy")
          throw new SharePolicyError();
        throw e;
      }
      const link = readLink(v);
      const url = str(obj(v)?.url);
      if (!link || !url) throw new ApiError("NET-SERVER", 201, "share create response", v);
      return { link, url: absoluteUrl(url, origin) };
    },
    async revoke(linkId) {
      return readLink(await request<unknown>(`${c}/${encodeURIComponent(linkId)}/revoke`, post()));
    },
  };
}

export function useShareTeacherApi(): ShareTeacherApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const courseId =
    state.status === "ready" && state.session.role === "teacher" ? state.session.courseId : null;
  return useMemo(
    () => (courseId ? createShareTeacherApi(manager.request, courseId) : null),
    [manager, courseId],
  );
}

// ── 관찰자 ──────────────────────────────────────────────────────────────

export function readShareOpen(v: unknown, apiBase = ""): ShareOpen {
  const o = obj(v) ?? {};
  if (o.enabled !== true) return { kind: "off" };
  if (o.session !== true) return { kind: "campus", campusUrl: str(o.campusUrl) };
  const video = obj(o.video);
  const replay = obj(o.replay);
  const vurl = str(video?.url);
  return {
    kind: "ok",
    view: {
      course: str(obj(o.course)?.label) ?? "",
      activity: str(obj(o.activity)?.title) ?? "",
      child: str(obj(o.child)?.nickname) ?? "",
      target: o.target === "replay" ? "replay" : "video",
      expiresAt: str(o.expiresAt),
      video: vurl
        ? {
            url:
              vurl.startsWith("/") && !vurl.startsWith("//")
                ? `${apiBase.replace(/\/+$/, "")}${vurl}`
                : vurl,
            durationS: num(video?.durationS),
          }
        : null,
      replay: str(replay?.replayId)
        ? { replayId: str(replay?.replayId)!, format: str(replay?.format) }
        : null,
      otherNames: o.otherNames === "all" ? "all" : "friend",
    },
  };
}

export function readShareReplay(v: unknown): ShareReplay {
  const o = obj(v) ?? {};
  const list = Array.isArray(o.machines) ? o.machines : [];
  return {
    replayId: str(o.replayId) ?? "",
    format: str(o.format),
    snapshotIds: Array.isArray(o.snapshotIds)
      ? o.snapshotIds.filter((s): s is string => typeof s === "string")
      : [],
    otherNames: o.otherNames === "all" ? "all" : o.otherNames === "friend" ? "friend" : null,
    meta: obj(o.meta),
    machines: list
      .map((m) => obj(m))
      .filter((m): m is Record<string, unknown> => !!m && num(m.machine) !== null)
      .map((m) => ({
        machine: num(m.machine)!,
        snapshotId: str(m.snapshotId),
        nickname: str(m.nickname),
        mine: m.mine === true,
      })),
  };
}

export interface ShareObserverApi {
  open(token: string): Promise<ShareOpen>;
  replay(token: string): Promise<ShareReplay>;
  replayFile(token: string): Promise<Uint8Array>;
  bundle(token: string, snapshotId: string): Promise<RunBundle>;
}

export function createShareObserverApi(request: BytesRequester, apiBase = ""): ShareObserverApi {
  const p = (token: string) => `/api/share/${encodeURIComponent(token)}`;
  return {
    async open(token) {
      try {
        return readShareOpen(await request<unknown>(p(token)), apiBase);
      } catch (e) {
        if (e instanceof ApiError) {
          const b = obj(e.body);
          // friend 정책의 영상 링크(410 policy)는 취소와 같게 본다(보호자에게 정책 사정은 필요 없다)
          if (e.status === 410)
            return { kind: "gone", state: b?.state === "expired" ? "expired" : "revoked" };
          if (e.status === 404) return { kind: "gone", state: "unknown" };
          if (e.status === 403)
            return {
              kind: "denied",
              reason: b?.reason === "org" ? "org" : b?.reason === "role" ? "role" : "child",
            };
        }
        throw e;
      }
    },
    replay: async (token) => readShareReplay(await request<unknown>(`${p(token)}/replay`)),
    replayFile: (token) => request<Uint8Array>(`${p(token)}/replay/file`, { as: "bytes" }),
    bundle: (token, id) =>
      request<RunBundle>(`${p(token)}/replay/bundles/${encodeURIComponent(id)}`),
  };
}

export function useShareObserverApi(): ShareObserverApi {
  const manager = useSessionManager();
  return useMemo(
    () => createShareObserverApi(manager.optionalRequest, readConfig().apiBase),
    [manager],
  );
}

/** 관찰자 화면의 다른 아이 이름(5-6 · Q-4) — friend 면 내 아이만 별명, 나머지는 '친구' */
export function observerLabels(
  machines: readonly ShareMachine[],
  policy: OtherNames,
  friend: string,
): Map<number, { label: string; mine: boolean }> {
  const out = new Map<number, { label: string; mine: boolean }>();
  for (const m of machines) {
    const name = m.mine || policy === "all" ? m.nickname : null;
    out.set(m.machine, { label: name ?? friend, mine: m.mine });
  }
  return out;
}
